import { AllTransactionType, NamedEntity, OptionalTransactionType, TransactionType, LedgerTransaction } from "../global/types";
import _ from "lodash";
import store from "../store";
import { selectSettings } from "../store/authSlice";
import { selectAllFinancialCategories } from "../store/userDataSlice";

export const getTransactionTypeByCategory = (categoryId: string | null): AllTransactionType | null => {
    if (!categoryId) return null;
    const allCategories = selectAllFinancialCategories(store.getState()) || [];
    const found = allCategories.find(cat => cat.id === categoryId);

    if (!found) return null;
    return found.category as AllTransactionType;
}

/**
 * Maps a category/categoryGroup value (TransactionType | OptionalTransactionType, as picked in the
 * transaction form) to the stricter TransactionType required by LedgerTransaction.type.
 * OptionalTransactionType budget-plan buckets (EXPENSES, SAVINGS, INVESTMENTS, GUILT_FREE) have no
 * ledger-level equivalent — they still net out as a plain EXPENSE for balance purposes.
 */
export const deriveTransactionType = (category: AllTransactionType | null | undefined): TransactionType => {
    if (category && (Object.values(TransactionType) as string[]).includes(category)) {
        return category as TransactionType;
    }
    return TransactionType.EXPENSE;
}


export const calculateIncomeAmount = (tx: Pick<LedgerTransaction, 'amount' | 'taxRate' | 'isBeforeTax'>, isBeforeTax?: boolean, defaultTaxRate?: number): number => {
    const defaultRate = _.isUndefined(defaultTaxRate) ? selectSettings(store.getState())?.defaultTaxRate || 0 : 0;
    const rate = (tx.taxRate || defaultRate) / 100;
    if (!_.isBoolean(tx.isBeforeTax)) return tx.amount;

    if (isBeforeTax) {
        return tx.isBeforeTax ? tx.amount : tx.amount / (1 - rate);
    }

    return tx.isBeforeTax ? tx.amount * (1 - rate) : tx.amount;
}

export const getSubCategoryById = (subCatId: string | null): NamedEntity | null => {
    if (!subCatId) return null;
    const allCategories = selectAllFinancialCategories(store.getState()) || [];
    return allCategories.find(cat => cat.id === subCatId) || null;
}

/** Shape accepted by isInflowTransaction/isOutflowTransaction: enough of a transaction to classify its effect on a given account. */
export type TransactionFlowInput = {
    type: AllTransactionType | null | undefined;
    accountId?: string | null;
    transferAccountId?: string | null;
    transferLeg?: 'debit' | 'credit' | null;
};

/**
 * True when this transaction reduces the account balance: EXPENSE, BILLS, SUBSCRIPTIONS, DEBTS,
 */
export const isOutflowTransaction = (data: TransactionFlowInput): boolean => {
    const { type, accountId, transferLeg } = data;
    if (!type) return false;
    if (type === TransactionType.TRANSFERS) {
        if (transferLeg) return transferLeg === 'debit';
        return !!accountId;
    }
    return [
        TransactionType.EXPENSE,
        TransactionType.BILLS,
        TransactionType.SUBSCRIPTIONS,
        TransactionType.DEBTS,
        OptionalTransactionType.EXPENSES,
        OptionalTransactionType.GUILT_FREE,
    ].includes(type);
}

/**
 * True when this transaction increases the account balance: INCOME, INITIAL_BALANCE.
 */
export const isInflowTransaction = (data: TransactionFlowInput): boolean => {
    const { type, transferAccountId, transferLeg } = data;
    if (!type) return false;
    if (type === TransactionType.TRANSFERS) {
        if (transferLeg) return transferLeg === 'credit';
        return !!transferAccountId;
    }
    return ([TransactionType.INCOME, TransactionType.INITIAL_BALANCE] as AllTransactionType[]).includes(type);
}

